import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';

import { Badge, Button } from '@/components';
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import {
  SiAttentionRider,
  SiAttentionRiders,
  SiSlotRider,
  scheduledInstantAPI,
} from '@/services/api/scheduled-instant';

import { ClientPicker } from './pickers';
import StandbyModal from './StandbyModal';
import { Avatar, Panel } from './shared';

type Tab = 'silent' | 'declined';

/**
 * Screen 15 — "Rider Belum Merespons".
 *
 * Two tabs, deliberately separate:
 *   Belum Merespons  NO_RESPONSE        pinged, said nothing → chase them
 *   Menolak          NEEDS_REPLACEMENT  tapped TIDAK         → replace them
 *
 * That split only works because the tick now writes NO_RESPONSE for silence
 * instead of NEEDS_REPLACEMENT. Until that landed, both tabs would have shown
 * identical rows.
 *
 * Not a sub-nav tab: the sub-nav holds work areas (Slot Harian / Master Jadwal /
 * Day Off Rider), and this screen is urgent and ideally EMPTY. It is entered
 * from the "Perlu Perhatian" stat card instead, and an empty result renders as
 * the healthy state rather than as an error.
 */
export const AttentionRidersView: React.FC<{
  date: string;
  onDateChange: (date: string) => void;
  client: string;
  onClientChange: (client: string) => void;
  onBack: () => void;
}> = ({ date, onDateChange, client, onClientChange, onBack }) => {
  const [tab, setTab] = useState<Tab>('silent');
  const [sendingId, setSendingId] = useState<string | null>(null);
  // Provider id only drives the picker's highlighted option; the client NAME is
  // what the endpoint filters on and what persists in the URL.
  const [filterProviderId, setFilterProviderId] = useState<number | ''>('');
  // "Cari Pengganti" reuses StandbyModal, which needs the slot's reserve riders
  // — those live on the slot detail, not on an attention row, so we fetch on
  // demand rather than over-fetching a detail per row up front.
  const [replaceFor, setReplaceFor] = useState<{
    slotId: string;
    slotLabel: string;
    target: { id: string; riderName: string };
    reserves: SiSlotRider[];
  } | null>(null);
  const [data, setData] = useState<SiAttentionRiders | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Re-render on a timer so the "sudah diam" elapsed column keeps counting up
  // instead of freezing at whatever it read when the page loaded.
  const [, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(
        await scheduledInstantAPI.listAttentionRiders({
          date,
          client: client || undefined,
        })
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [date, client]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  /** Manual WA nudge — same endpoint the Slot Harian rider panel uses. */
  const sendReminder = async (r: SiAttentionRider) => {
    setSendingId(r.slotRiderId);
    try {
      const res = await scheduledInstantAPI.sendRiderReminder(
        r.slotId,
        r.slotRiderId
      );
      if (res.inert) {
        showInfoToast(
          'Notifikasi WA nonaktif',
          'Aktifkan ENABLE_SI_NOTIFICATIONS di backend untuk mengirim.'
        );
      } else if (res.sent) {
        showSuccessToast('Reminder terkirim', `WA reminder ke ${r.riderName}`);
      } else {
        showErrorToast('Gagal kirim', `WA reminder ke ${r.riderName} gagal`);
      }
    } catch (e) {
      showErrorToast('Gagal', (e as Error).message);
    } finally {
      setSendingId(null);
    }
  };

  /** Open the existing replace flow; needs the slot's reserves. */
  const openReplace = async (r: SiAttentionRider) => {
    try {
      const detail = await scheduledInstantAPI.getSlot(r.slotId);
      setReplaceFor({
        slotId: r.slotId,
        slotLabel: `${r.clientName} · ${dayjs(r.scheduleDate).format(
          'DD MMM'
        )} · ${r.windowStartTime}–${r.windowEndTime}`,
        target: { id: r.slotRiderId, riderName: r.riderName },
        reserves: detail.reserveRiders ?? [],
      });
    } catch (e) {
      showErrorToast('Gagal membuka pengganti', (e as Error).message);
    }
  };

  const rows = data ? (tab === 'silent' ? data.silent : data.declined) : [];
  const isToday = date === dayjs().format('YYYY-MM-DD');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-500 hover:text-ink transition-colors flex items-center gap-1"
        >
          <i className="ri-arrow-left-s-line" /> Kembali ke Slot Harian
        </button>
        <span className="text-xs text-gray-400 tabular">
          {dayjs(date).format('DD MMM YYYY')}
        </span>
      </div>

      <div>
        <h2 className="text-lg font-bold text-ink">Rider Belum Merespons</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Rider yang sudah dikirimi T-12 atau T-2 tapi belum menekan tombol apa
          pun. Diurutkan dari yang jam pickup-nya paling dekat.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1">
          <Button
            variant="white"
            size="xs"
            onClick={() =>
              onDateChange(dayjs(date).subtract(1, 'day').format('YYYY-MM-DD'))
            }
            aria-label="Hari sebelumnya"
          >
            <i className="ri-arrow-left-s-line" />
          </Button>
          <span className="px-2 text-sm font-semibold text-ink tabular">
            {isToday ? 'Hari ini' : dayjs(date).format('ddd')} ·{' '}
            {dayjs(date).format('DD MMM YYYY')}
          </span>
          <Button
            variant="white"
            size="xs"
            onClick={() =>
              onDateChange(dayjs(date).add(1, 'day').format('YYYY-MM-DD'))
            }
            aria-label="Hari berikutnya"
          >
            <i className="ri-arrow-right-s-line" />
          </Button>
          {!isToday && (
            <Button
              variant="white"
              size="xs"
              onClick={() => onDateChange(dayjs().format('YYYY-MM-DD'))}
            >
              Hari ini
            </Button>
          )}
        </div>
        <div className="w-56">
          <ClientPicker
            open
            providerId={filterProviderId}
            clientName={client}
            placeholder="Filter klien…"
            onChange={(sel) => {
              setFilterProviderId(sel ? sel.providerId : '');
              onClientChange(sel ? sel.clientName : '');
            }}
          />
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {(
          [
            ['silent', 'Belum Merespons', data?.silentCount ?? 0],
            ['declined', 'Menolak (TIDAK)', data?.declinedCount ?? 0],
          ] as [Tab, string, number][]
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === key
                ? 'border-accent text-accent'
                : 'border-transparent text-gray-500 hover:text-ink'
            }`}
          >
            {label}
            <span className="ml-1.5 text-xs tabular text-gray-400">
              {count}
            </span>
          </button>
        ))}
      </div>

      {loading && (
        <Panel className="p-8 text-center text-sm text-gray-400">Memuat…</Panel>
      )}

      {error && !loading && (
        <Panel className="p-8 text-center">
          <div className="text-sm text-red-600">{error}</div>
          <Button className="mt-3" onClick={load}>
            Coba lagi
          </Button>
        </Panel>
      )}

      {!loading && !error && rows.length === 0 && (
        // Empty is the GOOD outcome here, so it reads as reassurance.
        <Panel className="p-10 text-center">
          <i className="ri-checkbox-circle-line text-3xl text-green-500" />
          <div className="mt-2 font-semibold text-ink">
            {tab === 'silent'
              ? 'Semua rider sudah merespons'
              : 'Tidak ada rider yang menolak'}
          </div>
          <div className="text-sm text-gray-400 mt-1">
            Tidak ada yang perlu ditindak untuk tanggal ini.
          </div>
        </Panel>
      )}

      {!loading && !error && rows.length > 0 && (
        <Panel>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="text-left px-4 py-2.5 font-semibold">Rider</th>
                <th className="text-left px-4 py-2.5 font-semibold">
                  Klien / Slot
                </th>
                <th className="text-left px-4 py-2.5 font-semibold">
                  Pesan Terakhir
                </th>
                <th className="text-left px-4 py-2.5 font-semibold">
                  {tab === 'silent' ? 'Sudah Diam' : 'Menolak Pada'}
                </th>
                <th className="text-left px-4 py-2.5 font-semibold">Pickup</th>
                <th className="text-right px-4 py-2.5 font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <AttentionRow
                  key={r.slotRiderId}
                  rider={r}
                  tab={tab}
                  sending={sendingId === r.slotRiderId}
                  onSendReminder={() => sendReminder(r)}
                  onReplace={() => openReplace(r)}
                />
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2.5 border-t border-gray-100 text-[11px] text-gray-400 tabular">
            Menampilkan {rows.length} rider
          </div>
        </Panel>
      )}

      {replaceFor && (
        <StandbyModal
          isOpen
          onClose={() => setReplaceFor(null)}
          slotId={replaceFor.slotId}
          slotLabel={replaceFor.slotLabel}
          target={replaceFor.target}
          reserves={replaceFor.reserves}
          onReplaced={() => {
            setReplaceFor(null);
            // The rider just left this list, so refetch rather than patching
            // local state — the counts on both tabs move too.
            load();
          }}
        />
      )}
    </div>
  );
};

/**
 * Back-office rider-detail URL, opened in a new tab when a rider is clicked.
 * The back office keys drivers by numeric id, which is the rider code's digits
 * without the MTR prefix and leading zeros (e.g. MTR0012000 → 12000). Returns
 * null when the base URL isn't configured or the code has no digits.
 */
const backOfficeRiderUrl = (riderCode: string): string | null => {
  const base = process.env.REACT_APP_BACK_OFFICE_URL;
  if (!base) return null;
  const id = parseInt(riderCode.replace(/\D/g, ''), 10);
  if (!Number.isFinite(id)) return null;
  return `${base.replace(/\/+$/, '')}/driver/${id}`;
};

const AttentionRow: React.FC<{
  rider: SiAttentionRider;
  tab: Tab;
  sending: boolean;
  onSendReminder: () => void;
  onReplace: () => void;
}> = ({ rider, tab, sending, onSendReminder, onReplace }) => (
  <tr className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
    <td className="px-4 py-3">
      <div className="flex items-center gap-2">
        <Avatar name={rider.riderName} />
        <div>
          {backOfficeRiderUrl(rider.riderCode) ? (
            <a
              href={backOfficeRiderUrl(rider.riderCode) as string}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-ink hover:text-accent hover:underline"
              title="Buka detail rider di back office"
            >
              {rider.riderName}
            </a>
          ) : (
            <div className="font-medium text-ink">{rider.riderName}</div>
          )}
          <div className="text-[11px] text-gray-400 tabular">
            {rider.riderCode}
          </div>
        </div>
      </div>
    </td>
    <td className="px-4 py-3">
      <div className="text-ink">{rider.clientName}</div>
      <div className="text-[11px] text-gray-400 tabular">
        {rider.windowStartTime}–{rider.windowEndTime}
      </div>
    </td>
    <td className="px-4 py-3">
      {rider.sendFailed ? (
        // A bad number is not an unresponsive rider — chasing them by WhatsApp
        // will never work, so say so instead of showing a silence timer.
        <Badge tone="warning" size="small">
          <i className="ri-error-warning-line mr-0.5" />
          Pesan gagal terkirim
        </Badge>
      ) : rider.lastPingPhase ? (
        <span className="text-gray-600 tabular">
          {rider.lastPingPhase === 'T12' ? 'T-12' : 'T-2'}
          {rider.lastPingAt && ` · ${dayjs(rider.lastPingAt).format('HH:mm')}`}
        </span>
      ) : (
        <span className="text-gray-300">—</span>
      )}
    </td>
    <td className="px-4 py-3">
      {tab === 'silent' ? (
        rider.sendFailed ? (
          <span className="text-gray-300">—</span>
        ) : (
          <span className="text-amber-600 font-semibold tabular">
            <i className="ri-time-line mr-0.5" />
            {elapsed(rider.silentSinceAt)}
          </span>
        )
      ) : (
        <span className="text-gray-600 tabular">
          {rider.respondedAt
            ? dayjs(rider.respondedAt).format('HH:mm')
            : '—'}
        </span>
      )}
    </td>
    <td className="px-4 py-3 tabular text-gray-600">
      {untilPickup(rider.scheduleDate, rider.windowStartTime)}
    </td>
    <td className="px-4 py-3">
      <div className="inline-flex items-center justify-end gap-1 w-full">
        {/* No point nudging a number that already bounced — offer only the
            replacement path for unreachable riders. */}
        {!rider.sendFailed && (
          <Button
            variant="ghost"
            size="xs"
            onClick={onSendReminder}
            disabled={sending}
          >
            <i className="ri-phone-line mr-1" />
            {sending ? 'Mengirim…' : 'Kirim WA'}
          </Button>
        )}
        <Button variant="outline-accent" size="xs" onClick={onReplace}>
          <i className="ri-arrow-left-right-line mr-1" />
          Cari Pengganti
        </Button>
      </div>
    </td>
  </tr>
);

/** Humanised elapsed time since `iso`. Null-safe: no ping → nothing to count. */
function elapsed(iso: string | null): string {
  if (!iso) return '—';
  const mins = dayjs().diff(dayjs(iso), 'minute');
  if (mins < 0) return '—';
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}j ${String(mins % 60).padStart(2, '0')}m`;
}

/**
 * Time until pickup, or how long ago it started. Slot windows are Jakarta
 * wall-clock and arrive as separate date + HH:mm, so they are combined here
 * rather than parsed as an instant.
 */
function untilPickup(scheduleDate: string, windowStartTime: string): string {
  const start = dayjs(`${scheduleDate}T${windowStartTime}`);
  if (!start.isValid()) return '—';
  const mins = start.diff(dayjs(), 'minute');
  if (mins < 0) return 'sudah mulai';
  if (mins < 60) return `${mins} menit lagi`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} jam lagi`;
  return start.format('DD MMM HH:mm');
}
